import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { WorkspaceSummary } from '../../../lib/workspaces';
import { Button, Typography } from '../ui';
import { WorkspaceBadge } from '../WorkspaceBadge';
import styles from './BriefSlot.module.css';

// "A new workspace created for you" — the slot on the first morning, when the brief routine is on but
// hasn't run yet. Announces the first workspace with the two shortcuts into it (native
// `BriefNewWorkspaceCard`: Growth / Agent work).

export function NewWorkspaceBrief({ workspace }: { workspace?: WorkspaceSummary }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  if (!workspace) return null;

  const path = `/workspace/${encodeURIComponent(workspace.slug)}`;
  const click = (button_name: string) => () => logPageClickBi({ page_name: 'home', button_name });

  return (
    <section className={styles.slot} aria-labelledby="brief-new-workspace-title">
      <h2 id="brief-new-workspace-title" className={`${styles.title} ${styles.titleFirst}`}>
        {t('brief.newWorkspace.title')}
      </h2>
      <div className={styles.workspace}>
        {/* Figma 13528-106217: a 20px workspace avatar beside the name at Display 04 (20/24). */}
        <WorkspaceBadge ws={workspace} size={20} />
        <Typography variant="h2" color="strong" component="p" className={styles.workspaceName}>
          {workspace.name}
        </Typography>
      </div>
      <div className={styles.actions}>
        <Button
          href={`${path}/agents`}
          endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
          onClick={click('brief_new_workspace_agent_work')}
        >
          {t('workspace.nav.agents')}
        </Button>
        <Button
          variant="outlined"
          href={`${path}/goals`}
          endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
          onClick={click('brief_new_workspace_goals')}
        >
          {t('workspace.nav.goals')}
        </Button>
        <Button
          variant="outlined"
          href={path}
          endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
          onClick={click('brief_new_workspace_growth')}
        >
          {t('workspace.nav.growth')}
        </Button>
      </div>
    </section>
  );
}
